/**
 * Unified KSA (Asia/Riyadh, UTC+3) time helpers — frontend source of truth.
 *
 * Operating policy (Feb 2026): every comparison, schedule decision, "today"
 * derivation, and user-facing date/time string in MESP is computed in
 * Asia/Riyadh. KSA and Yemen both observe UTC+3 with no DST, making
 * `Asia/Riyadh` the stable canonical IANA zone for both audiences.
 *
 * USE these helpers instead of:
 *   - `new Date().toISOString().split('T')[0]`  (returns UTC date, NOT KSA)
 *   - `new Date().toLocaleString()`              (uses BROWSER timezone)
 *   - `new Date().toLocaleDateString()`          (uses BROWSER timezone)
 *
 * Each user in KSA (+03), Yemen (+03), UAE (+04), or anywhere else will see
 * the SAME date and time strings — matching the operations team's wall clock.
 */
import { parseServerDate } from './parseServerDate';

export const OPERATING_TZ = 'Asia/Riyadh';

/**
 * Today's calendar date in KSA as the canonical ISO 8601 `YYYY-MM-DD`.
 * Useful for default values of `<input type="date">`, daily filters, and
 * any string sent to the backend that represents "today".
 *
 * Uses Intl.DateTimeFormat with `en-CA` locale because that locale's
 * `dateStyle: 'short'` (and the default 4-2-2 numeric format) emits
 * exactly `YYYY-MM-DD` — saves us a manual reformat.
 */
export const todayKSA = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: OPERATING_TZ }).format(new Date());

/**
 * Convert a stored UTC ISO datetime (e.g. `"2026-06-10T15:42:00Z"`) into a
 * human-readable date+time in KSA wall clock.
 *
 * `lang` controls only the locale of the rendered string ('ar' → Arabic
 * digits/labels, otherwise English); the underlying timezone is always KSA.
 */
export const formatDateTimeKSA = (isoOrDate, lang = 'en') => {
  if (!isoOrDate) return '';
  // Feb 2026 time audit: parseServerDate normalizes naive ISO strings
  // to UTC so a subsequent `.toLocaleString({timeZone: KSA})` renders
  // the correct wall-clock instant. Without this, notification bells,
  // audit logs and any KSA-formatted date drifted by 3–13 hours
  // depending on the browser's local timezone.
  const d = isoOrDate instanceof Date ? isoOrDate : parseServerDate(isoOrDate);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-GB', {
    timeZone: OPERATING_TZ,
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

/**
 * Format a stored datetime as a KSA date-only string ("10/06/2026" style).
 */
export const formatDateKSA = (isoOrDate, lang = 'en') => {
  if (!isoOrDate) return '';
  const d = isoOrDate instanceof Date ? isoOrDate : parseServerDate(isoOrDate);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-GB', {
    timeZone: OPERATING_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Format a stored datetime as a KSA time-only string ("18:30" style).
 */
export const formatTimeKSA = (isoOrDate, lang = 'en') => {
  if (!isoOrDate) return '';
  const d = isoOrDate instanceof Date ? isoOrDate : parseServerDate(isoOrDate);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(lang === 'ar' ? 'ar-SA' : 'en-GB', {
    timeZone: OPERATING_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};
